/**
 * iPad: Parts (qty, part no., # replaced, description — never prices) and the Field Report
 * entries (lubricants, mileage, meals, motel). Everything is queued, so it works offline.
 */
import { useState } from 'react';
import { newClientId } from './db';
import { enqueueItem } from './queue';
import type { MechForms, MechJob } from './types';
import { Big, BigField, Label, Sheet, bigInput } from './ui';

type SetForms = (fn: (f: MechForms) => MechForms) => void;
const QUICK_REASONS = ['Wrong part', 'Not used', 'Entered twice'];

export function PartsSheet({ job, userId, setForms, onClose }: { job: MechJob; userId: number; setForms: SetForms; onClose: () => void }) {
  const parts = job.forms.parts;
  const [qty, setQty] = useState('1');
  const [partNo, setPartNo] = useState('');
  const [replaced, setReplaced] = useState('');
  const [description, setDescription] = useState('');
  const [removing, setRemoving] = useState<number | string | null>(null);
  const canEdit = job.status === 'ASSIGNED' || job.status === 'IN_PROGRESS' || job.status === 'ON_HOLD';
  const canAdd = canEdit || job.status === 'READY_FOR_REVIEW';
  const qtyOk = /^\d{1,8}(\.\d{1,2})?$/.test(qty) && Number(qty) > 0;

  const add = async () => {
    const clientId = newClientId();
    await enqueueItem(userId, `Part #${job.number}`, { type: 'part_add', workOrderId: job.id, qty, partNo: partNo.trim() || null, replacedPartNo: replaced.trim() || null, description: description.trim() }, clientId);
    setForms((f) => ({
      ...f,
      parts: {
        noPartsUsed: false,
        lines: [...f.parts.lines, { id: -Date.now(), clientId, qty, partNo: partNo.trim() || null, replacedPartNo: replaced.trim() || null, description: description.trim(), source: 'MECHANIC', lateAdd: job.status === 'READY_FOR_REVIEW', removed: false, removedReason: null, createdAt: new Date().toISOString() }],
      },
    }));
    setQty('1');
    setPartNo('');
    setReplaced('');
    setDescription('');
  };

  const remove = async (key: number | string, reason: string) => {
    const line = parts.lines.find((l) => (l.id > 0 ? l.id : l.clientId) === key)!;
    await enqueueItem(userId, `Remove part #${job.number}`, { type: 'part_remove', workOrderId: job.id, ...(line.id > 0 ? { lineId: line.id } : { lineClientId: line.clientId! }), reason });
    setForms((f) => ({ ...f, parts: { ...f.parts, lines: f.parts.lines.filter((l) => l !== line) } }));
    setRemoving(null);
  };

  const noParts = async () => {
    const value = !parts.noPartsUsed;
    await enqueueItem(userId, `No parts used #${job.number}`, { type: 'no_parts_used', workOrderId: job.id, value });
    setForms((f) => ({ ...f, parts: { ...f.parts, noPartsUsed: value } }));
  };

  return (
    <Sheet title={`Parts — #${job.number}`} onClose={onClose}>
      {parts.lines.length === 0 && !parts.noPartsUsed && <p className="mb-3 text-lg text-slate-600">No parts yet.</p>}
      <ul className="mb-4 space-y-2">
        {parts.lines.map((l) => {
          const key = l.id > 0 ? l.id : l.clientId!;
          return (
            <li key={String(key)} className="rounded-xl bg-slate-100 p-3">
              <div className="flex items-start gap-3">
                <span className="text-2xl font-black">{Number(l.qty)}×</span>
                <div className="flex-1 text-lg">
                  <div className="font-bold">{l.description}</div>
                  <div className="text-base text-slate-600">
                    {l.partNo ? `Part ${l.partNo}` : 'No part number'}
                    {l.replacedPartNo && ` · replaced ${l.replacedPartNo}`}
                    {l.lateAdd && <span className="ml-2 rounded bg-amber-300 px-1 font-semibold text-black">Late add</span>}
                  </div>
                </div>
                {canEdit && l.source === 'MECHANIC' && <Big tone="light" className="min-h-[56px] border-2 border-slate-300" onClick={() => setRemoving(key)}>Remove</Big>}
              </div>
              {removing === key && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {QUICK_REASONS.map((r) => <Big key={r} tone="stop" className="min-h-[56px] px-2 text-base" onClick={() => remove(key, r)}>{r}</Big>)}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {canAdd && (
        <div className="rounded-2xl border-2 border-slate-300 p-3">
          <Label>Add a part</Label>
          <div className="flex items-center gap-2">
            <Big className="min-w-[64px] text-3xl" onClick={() => setQty(String(Math.max(1, Math.floor(Number(qty) || 1) - 1)))}>−</Big>
            <input inputMode="decimal" aria-label="Qty" className={`${bigInput} w-28 text-center text-2xl font-bold`} value={qty} onChange={(e) => setQty(e.target.value.replace(/[^\d.]/g, ''))} />
            <Big className="min-w-[64px] text-3xl" onClick={() => setQty(String(Math.floor(Number(qty) || 0) + 1))}>+</Big>
            <span className="text-lg text-slate-600">Qty</span>
          </div>
          <BigField label="Part No." value={partNo} onChange={(e) => setPartNo(e.target.value)} autoCapitalize="characters" />
          <BigField label="# Replaced (old part no.)" value={replaced} onChange={(e) => setReplaced(e.target.value)} autoCapitalize="characters" />
          <BigField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} autoCapitalize="sentences" autoCorrect="on" spellCheck placeholder="e.g. Fuel filter" />
          <p className="mt-1 text-base text-slate-600">🎤 You can tap the microphone on the keyboard to say it.</p>
          <Big tone="go" className="mt-3 w-full" disabled={!description.trim() || !qtyOk} onClick={add}>Add Part</Big>
        </div>
      )}

      {canEdit && (
        <Big tone={parts.noPartsUsed ? 'go' : 'light'} className="mt-4 w-full border-2 border-slate-300" disabled={parts.lines.length > 0} onClick={noParts}>
          {parts.noPartsUsed ? '✓ No parts used (tap to undo)' : 'No parts used on this job'}
        </Big>
      )}
      <p className="mt-3 text-base text-slate-600">Prices are filled in by the office.</p>
    </Sheet>
  );
}

export function FieldReportSheet({ job, userId, setForms, onClose }: { job: MechJob; userId: number; setForms: SetForms; onClose: () => void }) {
  const fr = job.forms.fieldReport;
  const [f, setF] = useState({
    lubeEng: fr.lubeEng ?? '',
    lubeTrans: fr.lubeTrans ?? '',
    lubeHyd: fr.lubeHyd ?? '',
    lubeCoolant: fr.lubeCoolant ?? '',
    lubeOther: fr.lubeOther ?? '',
    mileage: fr.mileage ?? '',
    meals: fr.meals ?? '',
    motel: fr.motel ?? '',
  });
  const set = (k: keyof typeof f, num = false) => (e: { target: { value: string } }) => setF({ ...f, [k]: num ? e.target.value.replace(/[^\d.]/g, '') : e.target.value });
  const numOk = (v: string, frac: number) => v === '' || new RegExp(`^\\d{1,9}(\\.\\d{1,${frac}})?$`).test(v);
  const valid = numOk(f.mileage, 1) && numOk(f.meals, 2) && numOk(f.motel, 2);
  return (
    <Sheet title={`Field Report — #${job.number}`} onClose={onClose}>
      <p className="text-lg text-slate-600">Labor start and finish come from your clock in / out.</p>
      <Label>Lubricants</Label>
      <div className="grid grid-cols-2 gap-x-3">
        <BigField label="Eng." value={f.lubeEng} onChange={set('lubeEng')} />
        <BigField label="Trans." value={f.lubeTrans} onChange={set('lubeTrans')} />
        <BigField label="Hyd." value={f.lubeHyd} onChange={set('lubeHyd')} />
        <BigField label="Coolant" value={f.lubeCoolant} onChange={set('lubeCoolant')} />
      </div>
      <BigField label="Other" value={f.lubeOther} onChange={set('lubeOther')} />
      <div className="grid grid-cols-3 gap-x-3">
        <BigField label="Mileage" inputMode="decimal" value={f.mileage} onChange={set('mileage', true)} />
        <BigField label="Meals $" inputMode="decimal" value={f.meals} onChange={set('meals', true)} />
        <BigField label="Motel $" inputMode="decimal" value={f.motel} onChange={set('motel', true)} />
      </div>
      <Big tone="go" className="mt-4 w-full" disabled={!valid} onClick={async () => {
        await enqueueItem(userId, `Field Report #${job.number}`, { type: 'field_report', workOrderId: job.id, ...f });
        setForms((x) => ({ ...x, fieldReport: { ...x.fieldReport, ...Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v || null])) } }));
        onClose();
      }}>Save</Big>
    </Sheet>
  );
}
